import { Box, FormHelperText } from "@mui/material";
import { MprcContainer, MprcFormSubHeader } from "components";
import { MprcNumberField } from "components/MprcNumberField";
import { MprcSwitch } from "components/MprcSwitch";
import { ManagedMeetingDto, ManagedMeetingOnlineLocationDto, MeetingType } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////////

interface IData {
    assignPresenceAfterViewRecording: boolean;
    hasScore: boolean;
    meetingType: MeetingType;
    onlineLocation: ManagedMeetingOnlineLocationDto | null;
}
interface IPros {
    visible: boolean;
    readonly?: boolean;
    viewData: IData;
    viewErrors: ApiProblemErrors | undefined;

    onFormUpdate: (dto: Partial<ManagedMeetingDto>) => void;
    onFormValidation: (valid: boolean, hasError: boolean) => void;
}

interface IValidationFields {
    assignPresenceAfterViewRecording: boolean;
    hasScore: boolean;
}

interface IPostValidation {
    assignPresenceAfterViewRecording: string[] | undefined;
    hasScore: string[] | undefined;
}

////////////////////////////////////////////////////////////////////////////////

function getDefaultValidate(currentValidation: IValidationFields, dto: IData): IValidationFields {
    var obj = {
        ...currentValidation,
        mandatoryAttendance: true,
        assignPresenceAfterViewRecording: true,
        hasScore: true,
    } as IValidationFields;

    return obj;
}

/////////////////////////////////////////////////////////////////////////////////

const EditMeetingPresenceBoxComponent: React.FC<IPros> = (props) => {
    const { visible, viewData, viewErrors, readonly = false, onFormUpdate, onFormValidation } = props;

    const { t } = useTranslation("lms");

    const [formValidation, setFormValidation] = useState<IValidationFields>(
        getDefaultValidate({} as IValidationFields, viewData)
    );

    const vErrors = viewErrors ?? {};
    const postValidation: IPostValidation = {
        assignPresenceAfterViewRecording: vErrors["assignPresenceAfterViewRecording"],
        hasScore: vErrors["hasScore"],
    };

    /////////////////////////////////////////////////////////////////////////////////

    ///
    const handleUpdateRecord = (name: keyof ManagedMeetingDto, v: any) => {
        var newViewData = { ...viewData, [name]: v };
        onFormUpdate(newViewData);
        setFormValidation((state) => getDefaultValidate(state, newViewData));
    };

    ///
    const handleUpdateOnlineLocation = (name: keyof ManagedMeetingOnlineLocationDto, v: any) => {
        var newData = viewData.onlineLocation ? { ...viewData.onlineLocation, [name]: v } : { [name]: v } as ManagedMeetingOnlineLocationDto;
        handleUpdateRecord("onlineLocation", newData);
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECTS
    ////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        onFormValidation(
            Object.values(formValidation).filter((x) => x === false).length == 0,
            Object.values(postValidation).filter(x => x !== undefined).length > 0
        );
    }, [formValidation, viewErrors]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Box sx={{ display: visible ? "block" : "none" }}>
            <MprcContainer margin="large">
                <MprcContainer>
                    <MprcSwitch
                        label={t("Meeting.PresenceBox.AssignPresenceAfterViewRecordingLabel")}
                        checked={viewData.assignPresenceAfterViewRecording}
                        disabled={readonly}
                        onChange={(e) => handleUpdateRecord("assignPresenceAfterViewRecording", e.target.checked)}
                        helperText={
                            <>
                                {t("Meeting.PresenceBox.AssignPresenceAfterViewRecordingHintL1")}<br />
                                {t("Meeting.PresenceBox.AssignPresenceAfterViewRecordingHintL2")}
                            </>
                        }
                    />
                </MprcContainer>
                <MprcContainer>
                    <MprcSwitch
                        label={t("Meeting.PresenceBox.HasScoreLabel")}
                        checked={viewData.hasScore}
                        disabled={readonly}
                        onChange={(e) => handleUpdateRecord("hasScore", e.target.checked)}
                        helperText={t("Meeting.PresenceBox.HasScoreHint")}
                    />
                </MprcContainer>
            </MprcContainer>
            {(viewData.meetingType == MeetingType.Teams || viewData.meetingType == MeetingType.Zoom) &&
                <MprcContainer>
                    <MprcFormSubHeader title={t("Meeting.PresenceBox.MinPermanenceSubtitle")} />
                    <MprcNumberField
                        disabled={readonly}
                        value={viewData.onlineLocation?.minPermanence ?? 0}
                        onChange={value => handleUpdateOnlineLocation("minPermanence", value)}
                        min={0}
                        max={100}
                        slotProps={{ input: { endAdornment: "%" } }}
                        sx={{ width: 100 }}
                    />
                    <FormHelperText>{t("Meeting.PresenceBox.MinPermanenceHint")}</FormHelperText>
                </MprcContainer>
            }
        </Box>
    );
};

export const EditMeetingPresenceBox = React.memo(EditMeetingPresenceBoxComponent, (prev, next) => {
    return LibUtils.deepCompare(prev.viewData, next.viewData)
        && prev.visible == next.visible
        && prev.readonly == next.readonly
        && prev.viewErrors == next.viewErrors;
    
});
